<template>
    <div>
        <b-card :header="tableHeader" header-class="card-header">
            <slot name="pre-filter"></slot>
            <b-row>
                <b-col>
                    <b-form-group :label="$t('tableBase.filter')">
                        <b-form-input
                            v-model="filter"
                            type="search"
                            id="filterInput"
                            :placeholder="$t('tableBase.placeholder')"
                            debounce="200"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="auto">
                    <b-form-group :label="$t('tableBase.rows')">
                        <b-form-select v-model="rowsPerPage" :options="[5, 10, 15]"/>
                    </b-form-group>
                </b-col>
                <b-col cols="auto">
                    <b-form-group :label="$t('tableBase.refresh')">
                        <b-button
                            variant="outline"
                            style="border: 1px solid #ced4da"
                            @click="$refs.table.refresh()"
                        >
                            <i class="fas fa-sync-alt"></i>
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>

            <div v-if="showExtraFields" class="extra-fields">
                <b-button v-b-toggle.collapse-1 variant="primary">{{ $t('tableBase.moreColumns') }}</b-button>
                <b-collapse id="collapse-1" class="mt-2">
                    <b-row>
                        <b-col>
                            <b-form-group :label="$t('tableBase.columnSelector') + ':'">
                                <b-form-checkbox-group
                                    id="checkbox-group-1"
                                    v-model="selectedField"
                                    :options="optionalFields"
                                    value-field="key"
                                    text-field="label"
                                    name="flavour-1"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-collapse>
            </div>

            <b-table
                id="table"
                ref="table"
                class="mt-3"
                :items="getItems"
                :fields="fields"
                :filter="filter"
                :per-page="rowsPerPage"
                :current-page="page"
                :busy="isBusy"
                :no-local-sorting="true"
                :responsive="true"
                tbody-tr-class="base-table-pointer-row"
                v-on="$listeners"
                show-empty
                hover
                :empty-text="$t('tableBase.noResults')"
            >
                <template v-slot:table-busy>
                    <base-spinner/>
                </template>

                <template v-slot:cell(state)="data">
                    <i
                        :title="$t('patient.' + data.item.state)"
                        :class="data.item.state"
                        class="fas fa-square"
                        style="font-size: 1.2rem;"
                    />
                </template>
            </b-table>
            <b-row align-h="between">
                <b-col>
                    <p>{{ $t('tableBase.showingResults', {showed: this.items.results.length, total: resultRows}) }}</p>
                </b-col>

                <b-col>
                    <b-pagination
                        v-model="page"
                        :total-rows="resultRows"
                        :per-page="rowsPerPage"
                        aria-controls="table"
                        align="right"
                    />
                </b-col>
            </b-row>
            <p v-if="errorMessage !== ''" class="error">{{ errorMessage }}</p>

            <slot></slot>

        </b-card>
    </div>
</template>

<script>
    import BaseSpinner from '@/components/BaseSpinner';
    import * as Sentry from "@sentry/browser";

    export default {
        name: "BaseTable",
        components: {
            BaseSpinner
        },
        props: {
            vuexState: {
                type: String,
                required: true,
            },
            vuexAction: {
                type: String,
                required: true,
            },
            fixedFields: {
                type: Array,
                default: () => [],
            },
            optionalFields: {
                type: Array,
                default: () => [],
            },
            tableHeaderTranslationKey: {
                type: String,
                default: 'tableBase.header',
            }
        },
        data() {
            return {
                errorMessage: '',
                filter: '',
                selectedField: [],
                rowsPerPage: 5,
                page: 1,
                isBusy: false,
                sortBy: null,
                sortDesc: false,
            };
        },
        computed: {
            items() {
                return this.$store.state[this.vuexState];
            },
            resultRows() {
                return this.items.count;
            },
            fields() {
                let self = this;
                if (this.hasExtraFields && this.fixedFields.length === 0) {
                    Sentry.captureMessage('You must provide fixed fields if you want to add optional fields');
                    return this.fixedFields;
                } else {
                    const optionals = this.optionalFields.filter(element => self.selectedField.includes(element.key));
                    return [...this.fixedFields, ...optionals];
                }

            },
            hasExtraFields() {
                return this.optionalFields.length !== 0;
            },
            showExtraFields() {
                return this.hasExtraFields && this.fixedFields.length !== 0;
            },
            tableHeader() {
                return this.$t(this.tableHeaderTranslationKey);
            }
        },
        methods: {
            getVuexItems(params) {
                return this.$store.dispatch(this.vuexAction, params);
            },
            getItems(context) {
                // calback for datatable items is only called with context parameter
                // thus we can't access vue instance, so this wrapper
                return this.getItemsWithContext(context);
            },
            getItemsWithContext(context) {
                let params = {
                    search: this.filter,
                    pagesize: this.rowsPerPage,
                    page: this.page,
                };
                if (context.sortBy !== '') {
                    const prefix = context.sortDesc ? '-' : '';
                    params['ordering'] = prefix + context.sortBy;
                }
                this.isBusy = true;
                const self = this;
                return this.getVuexItems(params)
                    .then(() => self.items.results)
                    .finally(() => self.isBusy = false);
            },
        },
    };
</script>

<style lang="scss">
    .base-table-pointer-row {
        cursor: pointer;
    }
</style>